import { useEffect, useMemo, useRef, useState } from "react";
import type { Scene, Story } from "../types/story";
import { loadCatalog, loadStory } from "../lib/loadStory";
import { makeNavigator, type Navigator } from "../lib/pathing";
import {
  resolveBase,
  resolveSession,
  hasStoryParam,
  type SessionBase,
  type SessionConfig,
} from "../lib/params";
import { getReadStoryIds, markStoryRead } from "../lib/library";
import { Telemetry } from "../lib/telemetry/queue";
import { preloadStoryAssets } from "../lib/preloadAssets";
import { useSceneAudio } from "../lib/useSceneAudio";
import { useMediaQuery } from "../lib/useMediaQuery";
import { useMood } from "../lib/useMood";
import { sound } from "../lib/sound";
import { hapticTick } from "../lib/haptics";
import { BookView } from "./BookView";
import { Controls } from "./Controls";
import { ChoicePrompt } from "./ChoicePrompt";
import { Comprehension } from "./Comprehension";
import { ExportPanel } from "./ExportPanel";
import { Incantation } from "./Incantation";
import { LibraryView, type LibraryStoryCard } from "./LibraryView";
import { NightSky, bumpKeepsake } from "./NightSky";
import { Stardust, stardust } from "./Stardust";
import { StoryStepper } from "./StoryStepper";

type Phase =
  | "loading"
  | "error"
  | "gate"
  | "library"
  | "scene"
  | "comprehension"
  | "end";

// Where relative asset paths resolve BEFORE a story loads (cover during the
// loading phase has none, so this default is never actually dereferenced). Once
// the story loads, App uses the `assetBase` loadStory returns — the site root
// in local dev, or the versioned CDN directory in production.
const DEFAULT_ASSET_BASE = import.meta.env.BASE_URL; // e.g. "/"

/** Page-turn duration; MUST match --turn-ms in book.css. */
const TURN_MS = 700;

/**
 * How long the weaving cover may hold "Start reading" asleep while a story's
 * assets pre-warm. A slow network caps out and the book opens anyway (images
 * then arrive as they can) — bedtime is never held hostage to a spinner.
 */
const ASSET_WAIT_CAP_MS = 10000;

export function App() {
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string>("");
  const [config, setConfig] = useState<SessionConfig | null>(null);
  const [story, setStory] = useState<Story | null>(null);
  // Prefix the story's relative asset paths resolve against — set from
  // loadStory (site root locally, versioned CDN directory in production).
  const [assetBase, setAssetBase] = useState<string>(DEFAULT_ASSET_BASE);
  const [nav, setNav] = useState<Navigator | null>(null);
  const [telemetry, setTelemetry] = useState<Telemetry | null>(null);
  // Ref mirror of the active session's telemetry so startSession can dispose the
  // previous one (a library visit opens several sessions) without leaking listeners.
  const telemetryRef = useRef<Telemetry | null>(null);

  // The library shelf: who is reading tonight (resolved before any story is
  // picked) and the books on offer. Empty unless we entered library mode.
  const [base, setBase] = useState<SessionBase | null>(null);
  const [libraryCards, setLibraryCards] = useState<LibraryStoryCard[]>([]);

  // Asset pre-warm for the weaving cover: the golden stitch's honest progress,
  // and whether "Start reading" has woken yet (see startSession).
  const [assetProgress, setAssetProgress] = useState(0);
  const [assetsReady, setAssetsReady] = useState(false);
  // Monotonic session ticket, so a previous story's pre-warm can never mark a
  // newly opened one ready (library → pick A → shelf → pick B).
  const sessionSeqRef = useRef(0);

  // A tap on the shelf button mid-story asks before abandoning the page.
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Mirrors confirmLeave for timer callbacks (an auto-advance ring must not turn
  // the page under the open dialog), plus whether to resume narration after.
  const confirmLeaveRef = useRef(false);
  confirmLeaveRef.current = confirmLeave;
  const resumeAfterLeaveRef = useRef(false);

  const [scene, setScene] = useState<Scene | undefined>(undefined);
  const [path, setPath] = useState<string[]>([]);

  // Page-turn transition: while turning we keep `scene` (the "from" page) and
  // hold the next scene in `pending` so both can render during the flip. We
  // commit (swap `scene`) only when the leaf animation ends.
  const [pending, setPending] = useState<Scene | null>(null);
  const pendingRef = useRef<Scene | null>(null);
  pendingRef.current = pending;
  const turnGuard = useRef(false);

  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  // Timing refs (avoid stale closures).
  const sceneEnterRef = useRef<number>(0);
  const storyStartRef = useRef<number>(0);
  const sceneRef = useRef<Scene | undefined>(undefined);
  sceneRef.current = scene;

  // Guards a double-advance from the SAME scene: the auto-advance countdown
  // timer and a manual tap on the ring can both fire before React commits the
  // next scene (sceneRef still points at the old scene). Without this, the
  // path / telemetry would record the scene twice and could skip a page.
  const advancedFromRef = useRef<string | null>(null);

  const exportRequested = useMemo(
    () => new URLSearchParams(window.location.search).get("export") === "1",
    []
  );

  // The child's first name is in the URL, so the opening incantation can begin
  // writing it in BEFORE the story file has finished loading — no dead spinner.
  const initialChild = useMemo(
    () => new URLSearchParams(window.location.search).get("child")?.trim() ?? "",
    []
  );

  // The keepsake night sky: how many stories this child has finished. Added once
  // per session, the first time we reach the end (a replay never re-counts).
  const [nights, setNights] = useState(0);
  const keepsakeAddedRef = useRef(false);

  // The labels of the paths this child chose tonight — for the end screen's
  // memory nod ("…and you chose The silver pond"). Authored text only.
  const [choiceLabels, setChoiceLabels] = useState<string[]>([]);

  // ---- Resolve the session, then show the library (or a deep-linked story) --
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const search = window.location.search;

        // Family + child identify the shelf + keepsake (the only PII); required
        // before anything else.
        const { base: who, error: baseErr } = resolveBase(search);
        if (!who) {
          setError(baseErr ?? "Could not resolve this link.");
          setPhase("error");
          return;
        }
        setBase(who);

        // A ?story= deep-link bypasses the shelf and opens that one story
        // directly (the study / passive-mode path). Otherwise: the library.
        if (hasStoryParam(search)) {
          const { config: cfg, error: resolveErr } = resolveSession(search);
          if (!cfg) {
            setError(resolveErr ?? "Could not resolve this link.");
            setPhase("error");
            return;
          }
          const { story: s, assetBase: ab } = await loadStory(
            cfg.storyId,
            ctrl.signal
          );
          // StrictMode dev double-invoke guard: if superseded, don't start a
          // second Telemetry / sessionStart.
          if (ctrl.signal.aborted) return;
          startSession(cfg, s, ab);
          return;
        }

        // The library shelf: titles from the catalog show at once, then each
        // book fills in (cover + pages) as its JSON loads. A book that fails to
        // load stays a disabled placeholder; the rest of the shelf still works.
        const stories = await loadCatalog(ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (stories.length === 0) {
          setError(
            "No stories are available yet. Open a ?story= link, or check the catalog."
          );
          setPhase("error");
          return;
        }
        const read = getReadStoryIds(who);
        setLibraryCards(
          stories.map((e) => ({
            storyId: e.storyId,
            title: (typeof e.title === "string" && e.title) || e.storyId,
            assetBase: null,
            story: null,
            read: read.has(e.storyId),
          }))
        );
        setPhase("library");
        for (const e of stories) {
          loadStory(e.storyId, ctrl.signal)
            .then(({ story: s, assetBase: ab }) => {
              if (ctrl.signal.aborted) return;
              setLibraryCards((cards) =>
                cards.map((c) =>
                  c.storyId === e.storyId ? { ...c, story: s, assetBase: ab } : c
                )
              );
            })
            .catch((err) => {
              if (ctrl.signal.aborted) return;
              console.warn(`Tella: library story "${e.storyId}" is resting —`, err);
            });
        }
      } catch (e) {
        if (ctrl.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to load story.");
        setPhase("error");
      }
    })();
    return () => ctrl.abort();
  }, []);

  // ---- Narration audio for the current scene -----------------------------
  const audioSrc =
    phase === "scene" && scene?.audio?.src
      ? resolveSrc(scene.audio.src, assetBase)
      : undefined;

  const audio = useSceneAudio(audioSrc, {
    autoPlay: true,
    onEnded: () => {
      const s = sceneRef.current;
      if (s && telemetry) void telemetry.audioCompleted(s.id);
    },
  });

  // Preload the next scene's illustration(s) so the page-turn has no image pop.
  useEffect(() => {
    if (!scene || !nav || !story) return;
    const ids: string[] = [];
    if (scene.choice?.options?.length) {
      for (const o of scene.choice.options) if (o?.nextSceneId) ids.push(o.nextSceneId);
    } else {
      const n = nav.next(scene.id);
      if (n) ids.push(n.id);
    }
    for (const id of ids) {
      const src = story.scenes.find((x) => x.id === id)?.image?.src;
      if (src) {
        const img = new Image();
        img.decoding = "async";
        img.src = resolveSrc(src, assetBase);
      }
    }
  }, [scene, nav, story, assetBase]);

  // ---- Mood-and-time layer ----------------------------------------------
  // How far we are through the story (0..1); the room tucks in as it climbs.
  const total =
    story?.canonicalPath?.length ?? story?.scenes.length ?? 1;
  const progress =
    phase === "scene" ? Math.min(1, (path.length - 1) / Math.max(1, total - 1)) : 0;
  const reading =
    phase === "scene" || phase === "comprehension" || phase === "end";
  useMood(progress, reading);

  // Keep the UI sound layer muted in lockstep with the narration's mute, so one
  // tap silences the whole room rather than just the words.
  useEffect(() => {
    sound.setMuted(audio.muted);
  }, [audio.muted]);

  // ---- Parallax depth ----------------------------------------------------
  // One loop writes --px/--py onto the root; the illustration inherits them and
  // drifts a few pixels behind its frame, so each page feels like a PLACE held
  // in the hand. Three inputs, best available wins: a recent pointer, device
  // tilt — but ONLY where it needs no permission prompt (never break the spell
  // with an OS dialog) — and otherwise a very slow autonomous float so the page
  // is alive even untouched. Everything is lerped so sources hand off softly.
  // Off entirely under reduced motion.
  useEffect(() => {
    if (reducedMotion || typeof window === "undefined") return;
    const root = document.documentElement;
    const MAX = 7;
    let raf = 0;
    let px = 0;
    let py = 0;
    let pointer: { x: number; y: number; at: number } | null = null;
    let tilt: { x: number; y: number } | null = null;
    let tilt0: { b: number; g: number } | null = null;

    const onMove = (e: PointerEvent) => {
      pointer = {
        x: -((e.clientX / window.innerWidth) * 2 - 1) * MAX,
        y: -((e.clientY / window.innerHeight) * 2 - 1) * MAX,
        at: performance.now(),
      };
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    const onTilt = (e: DeviceOrientationEvent) => {
      if (e.beta == null || e.gamma == null) return;
      if (!tilt0) tilt0 = { b: e.beta, g: e.gamma };
      // Re-center the calibration very slowly, so however the phone is held
      // tonight becomes the neutral pose and only RELATIVE tilt parallaxes.
      tilt0.b += (e.beta - tilt0.b) * 0.005;
      tilt0.g += (e.gamma - tilt0.g) * 0.005;
      const gx = Math.max(-14, Math.min(14, e.gamma - tilt0.g));
      const gy = Math.max(-14, Math.min(14, e.beta - tilt0.b));
      tilt = { x: -(gx / 14) * MAX, y: -(gy / 14) * MAX };
    };
    const DOE = (
      window as unknown as {
        DeviceOrientationEvent?: { requestPermission?: unknown };
      }
    ).DeviceOrientationEvent;
    const canTilt = !!DOE && typeof DOE.requestPermission !== "function";
    if (canTilt) window.addEventListener("deviceorientation", onTilt, true);

    const t0 = performance.now();
    const frame = (now: number) => {
      const t = (now - t0) / 1000;
      // The resting float: a slow lissajous orbit, like the page breathing.
      let tx = Math.sin(t * 0.21) * 2.6;
      let ty = Math.cos(t * 0.16) * 2.2;
      if (pointer && now - pointer.at < 2600) {
        tx = pointer.x;
        ty = pointer.y;
      } else if (tilt) {
        tx = tilt.x;
        ty = tilt.y;
      }
      px += (tx - px) * 0.045;
      py += (ty - py) * 0.045;
      root.style.setProperty("--px", `${px.toFixed(2)}px`);
      root.style.setProperty("--py", `${py.toFixed(2)}px`);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      window.removeEventListener("pointermove", onMove);
      if (canTilt) window.removeEventListener("deviceorientation", onTilt, true);
      cancelAnimationFrame(raf);
      root.style.removeProperty("--px");
      root.style.removeProperty("--py");
    };
  }, [reducedMotion]);

  // ---- Voice breath --------------------------------------------------------
  // The page breathes with the narrator's actual voice: an analyser on the
  // shared narration element writes a smoothed loudness to --voice (0..1),
  // which the reading lamp and focus word consume in CSS. Attached ONLY when
  // every scene's audio is same-origin — a cross-origin src routed through
  // WebAudio would fall silent (see sound.attachVoice). We test the RESOLVED
  // URL, not the raw `src`: relative paths served from the CDN (assetBase is a
  // cross-origin URL) are cross-origin too, so this correctly disables the
  // voice breath — graceful degradation, no CORS assumption on the CDN.
  const voiceOk = useMemo(() => {
    if (!story) return false;
    return story.scenes.every((s) => {
      const src = s.audio?.src;
      if (!src) return true;
      try {
        return (
          new URL(resolveSrc(src, assetBase), window.location.href).origin ===
          window.location.origin
        );
      } catch {
        return false;
      }
    });
  }, [story, assetBase]);

  useEffect(() => {
    const root = document.documentElement;
    if (!voiceOk || reducedMotion || phase !== "scene" || !audio.isPlaying) {
      root.style.setProperty("--voice", "0");
      return;
    }
    const el = audio.element;
    if (!el) return;
    const analyser = sound.attachVoice(el);
    if (!analyser) return;
    const data = new Uint8Array(analyser.fftSize);
    let level = 0;
    let raf = 0;
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 4) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / (data.length / 4));
      const target = Math.min(1, rms * 3.2);
      // Fast attack, slow release — the lamp brightens with a word and eases off.
      level += (target - level) * (target > level ? 0.4 : 0.08);
      root.style.setProperty("--voice", level.toFixed(3));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      root.style.setProperty("--voice", "0");
    };
  }, [voiceOk, reducedMotion, phase, audio.isPlaying, audio.element]);

  // ---- The living dusk -----------------------------------------------------
  // The mote layer stills itself while narration plays and exhales at beats.
  useEffect(() => {
    stardust.setCalm(phase === "scene" && audio.isPlaying);
  }, [phase, audio.isPlaying]);
  useEffect(() => {
    stardust.setNight(phase === "end");
  }, [phase]);

  // ---- Session lifecycle -------------------------------------------------
  /**
   * Open one story: set its asset base, build a Navigator + a fresh Telemetry
   * session, fire sessionStart, and cross to the gate (the cover). Shared by the
   * deep-link load path and a library pick. Disposing the previous session's
   * listeners first keeps a multi-story library visit from leaking them.
   */
  function startSession(cfg: SessionConfig, s: Story, ab: string) {
    const navigator = makeNavigator(s, cfg.mode);
    telemetryRef.current?.dispose();
    const tel = new Telemetry(cfg);
    telemetryRef.current = tel;

    setConfig(cfg);
    setStory(s);
    setAssetBase(ab);
    setNav(navigator);
    setTelemetry(tel);

    void tel.sessionStart();
    setPhase("gate");

    // Pre-warm this story's illustrations + its opening narration behind the
    // weaving cover, so the first page doesn't pop in. The golden stitch shows
    // real progress; the cap keeps bedtime prompt on a slow line.
    const seq = ++sessionSeqRef.current;
    setAssetsReady(false);
    setAssetProgress(0);
    const cap = window.setTimeout(() => {
      if (sessionSeqRef.current === seq) setAssetsReady(true);
    }, ASSET_WAIT_CAP_MS);
    void preloadStoryAssets(s, ab, (done, total) => {
      if (sessionSeqRef.current === seq) {
        setAssetProgress(total > 0 ? done / total : 1);
      }
    }).finally(() => {
      window.clearTimeout(cap);
      if (sessionSeqRef.current === seq) setAssetsReady(true);
    });
  }

  /** A book tapped on the shelf: open the already-loaded story behind it. */
  function pickStory(storyId: string) {
    if (!base) return;
    const card = libraryCards.find((c) => c.storyId === storyId);
    if (!card?.story || card.assetBase == null) return; // still weaving / failed
    startSession(
      { family: base.family, child: base.child, storyId, mode: "interactive" },
      card.story,
      card.assetBase
    );
  }

  /**
   * The shelf button, reachable from every phase. Mid-story it asks first — a
   * child's stray tap must not abandon the page — and stills the room while the
   * question stands.
   */
  function requestLibrary() {
    if (phase === "scene" || phase === "comprehension") {
      resumeAfterLeaveRef.current = audio.isPlaying;
      audio.pause();
      setConfirmLeave(true);
      return;
    }
    returnToLibrary();
  }

  /** "Keep reading": close the ask and pick the story back up where it stood. */
  function stayInStory() {
    setConfirmLeave(false);
    confirmLeaveRef.current = false; // the ref must not lag the state a frame
    // A page-turn frozen by the dialog completes now (its narration auto-plays
    // on commit); otherwise just resume what was paused.
    if (pendingRef.current) {
      commitTurn();
    } else if (resumeAfterLeaveRef.current) {
      resumeAfterLeaveRef.current = false;
      audio.play();
    }
  }

  /** Back to the shelf, the finished book now starred. */
  function returnToLibrary() {
    setConfirmLeave(false);
    confirmLeaveRef.current = false;
    // Leaving mid-scene: keep the dwell record honest and get the queue out.
    if (phase === "scene") recordDwell();
    if (telemetry) void telemetry.flush();
    audio.pause();
    turnGuard.current = false;
    stardust.breathe();
    if (base) {
      const read = getReadStoryIds(base);
      setLibraryCards((cards) =>
        cards.map((c) => ({ ...c, read: read.has(c.storyId) }))
      );
    }
    // Leave the just-read story's telemetry alive (its queue persists in
    // IndexedDB and its pagehide beacon still covers a straight exit); the next
    // pick disposes and replaces it.
    setScene(undefined);
    setPending(null);
    pendingRef.current = null;
    setPhase("library");
  }

  // ---- Navigation --------------------------------------------------------
  function beginStory() {
    if (!nav) return;
    const first = nav.start();
    if (!first) {
      finishStory([]);
      return;
    }
    const now = Date.now();
    storyStartRef.current = now;
    sceneEnterRef.current = now;
    turnGuard.current = false;
    pendingRef.current = null;
    setPending(null);
    advancedFromRef.current = null;
    setChoiceLabels([]);
    setPath([first.id]);
    setScene(first);
    setPhase("scene");
  }

  /** Record dwell for the scene we're leaving. */
  function recordDwell() {
    const s = sceneRef.current;
    if (s && telemetry) {
      const dwell = Date.now() - sceneEnterRef.current;
      void telemetry.sceneViewed(s.id, dwell);
    }
  }

  function advance(chosenOptionIndex?: number) {
    const s = sceneRef.current;
    if (!s || !nav) return;
    if (pendingRef.current) return; // ignore input mid-turn
    // The leave dialog is open: a stray auto-advance timer must not turn the page
    // underneath it. (advancedFromRef is untouched, so a tap after "Keep
    // reading" still works.)
    if (confirmLeaveRef.current) return;
    // Ignore a second advance from the same scene (countdown timer + tap race).
    if (advancedFromRef.current === s.id) return;
    advancedFromRef.current = s.id;

    recordDwell();
    // The confirming note + haptic fire in ChoicePrompt, timed to the bloom; here
    // we only record the choice (telemetry + the end screen's memory nod).
    if (chosenOptionIndex !== undefined) {
      if (telemetry) void telemetry.choiceMade(s.id, chosenOptionIndex);
      const label = s.choice?.options?.[chosenOptionIndex]?.label;
      if (label) setChoiceLabels((l) => [...l, label]);
    }

    const next = nav.next(s.id, chosenOptionIndex);
    if (!next) {
      finishStory(path);
      return;
    }

    stardust.breathe(); // the room exhales at the page boundary

    if (reducedMotion) {
      commitTo(next); // no page-turn: swap instantly
      return;
    }

    // Begin the page-turn: pause narration, render both pages, and commit when
    // the leaf finishes (with a timeout backstop in case animationend is missed).
    audio.pause();
    sound.pageSettle(); // a whisper of paper as the page lifts
    turnGuard.current = false;
    pendingRef.current = next;
    setPending(next);
    window.setTimeout(commitTurn, TURN_MS + 300);
  }

  /** Commit a page-turn: make the pending scene current. Runs at most once. */
  function commitTurn() {
    // Frozen while the leave dialog is open: committing would swap the scene and
    // AUTO-PLAY the next narration under the modal. `pending` stays set, so
    // stayInStory finishes the turn once the dialog closes.
    if (confirmLeaveRef.current) return;
    if (turnGuard.current) return;
    const next = pendingRef.current;
    if (!next) return;
    turnGuard.current = true;
    pendingRef.current = null;
    setPending(null);
    commitTo(next);
  }

  /** Make `next` the current scene and start its dwell clock. */
  function commitTo(next: Scene) {
    sceneEnterRef.current = Date.now();
    setPath((p) => [...p, next.id]);
    setScene(next);
    // phase stays "scene"
  }

  function finishStory(finalPath: string[]) {
    const hasComprehension =
      config?.mode === "interactive" &&
      Array.isArray(story?.comprehension) &&
      (story?.comprehension?.length ?? 0) > 0;

    if (hasComprehension) {
      setPhase("comprehension");
    } else {
      completeAndEnd(finalPath);
    }
  }

  function completeAndEnd(finalPath: string[]) {
    if (telemetry) {
      const totalMs = Date.now() - storyStartRef.current;
      void telemetry.storyCompleted(finalPath, totalMs);
    }
    // Add one star to this child's private night sky — once per session, the
    // first time they reach the end. A keepsake for the parent's felt sense of
    // progress, never a points-grind for the child.
    if (!keepsakeAddedRef.current) {
      keepsakeAddedRef.current = true;
      setNights(bumpKeepsake(config));
    }
    // Mark this story finished for its shelf star (per-child, device-local; a
    // gentle "you've been here", never a lock or a score).
    if (base && config) markStoryRead(base, config.storyId);
    setPhase("end");
  }

  /** The gate's "Start reading" tap: unlock + land the threshold note, then go. */
  function startReading() {
    sound.unlock();
    sound.coverNote();
    stardust.burst(0.5, 0.8, 22); // a small bloom of dust as the book opens
    beginStory();
  }

  function replay() {
    if (telemetry) void telemetry.replay();
    audio.pause();
    beginStory();
  }

  const turning = pending !== null;
  const isChoiceScene =
    !!scene &&
    config?.mode === "interactive" &&
    nav?.hasChoiceAt(scene.id) === true;
  const isLastScene = !!scene && !nav?.next(scene.id);
  const showChoice = phase === "scene" && isChoiceScene && audio.ended;
  // Swipe-left turns the page only when it's "turnable": narration finished
  // (matching the no-skip-during-narration controls), not a choice scene, not
  // the last scene (never swipe into comprehension), and not mid-turn.
  const canSwipeForward =
    phase === "scene" &&
    !!scene &&
    !turning &&
    !isChoiceScene &&
    !isLastScene &&
    audio.ended;

  // The story leans in to ask: the room exhales once as a choice opens.
  useEffect(() => {
    if (showChoice) stardust.breathe();
  }, [showChoice]);

  // ---- Render ------------------------------------------------------------
  let content: React.ReactNode;

  if (phase === "error") {
    content = (
      <Centered>
        <h1>We couldn’t open this story</h1>
        <p className="muted">{error}</p>
      </Centered>
    );
  } else if (phase === "loading" || phase === "gate") {
    // No dead spinner: the cover weaves itself in front of the child while the
    // story file loads, and "Start reading" wakes once the story is ready.
    content = (
      <Centered>
        <Incantation
          story={story}
          // Prefer the name the story was WOVEN for (a shelf may hold siblings'
          // books); before any story loads, the URL name writes itself in.
          child={story?.child?.name?.trim() || config?.child || initialChild}
          assetBase={assetBase}
          // Not just "the file arrived" — the art and the opening narration are
          // warm too, so the first page never pops in.
          ready={phase === "gate" && assetsReady}
          progress={assetProgress}
          onStart={startReading}
          onBrowse={libraryCards.length > 0 ? returnToLibrary : undefined}
        />
      </Centered>
    );
  } else if (phase === "library") {
    // The shelf: same lamplit room as the cover (the dusk veil stays warm behind).
    content = (
      <div className="app">
        <LibraryView
          childName={base?.child || initialChild}
          stories={libraryCards}
          onPick={pickStory}
        />
        {exportRequested && telemetry && (
          <div className="export-floating">
            <ExportPanel telemetry={telemetry} />
          </div>
        )}
      </div>
    );
  } else {
    const appCls = [
      "app",
      showChoice ? "app--choosing" : "",
      phase === "end" ? "app--night" : "",
    ]
      .filter(Boolean)
      .join(" ");
    content = (
      <div className={appCls}>
        <header className="app-header">
          {libraryCards.length > 0 && (
            <button
              type="button"
              className="nav-shelf"
              onClick={requestLibrary}
              aria-label="Back to the library"
            >
              <ShelfGlyph />
            </button>
          )}
          <span className="app-title">{story?.title}</span>
          {phase === "scene" && (
            <StoryStepper page={Math.min(path.length, total)} total={total} />
          )}
        </header>

        <main className="stage">
          {phase === "scene" && scene && (
            <>
              <BookView
                scene={scene}
                pending={pending}
                currentMs={audio.currentMs}
                assetBase={assetBase}
                onTurnEnd={commitTurn}
                onSwipeForward={canSwipeForward ? () => advance() : undefined}
              />

              {!turning &&
                (showChoice && scene.choice ? (
                  <ChoicePrompt
                    choice={scene.choice}
                    onChoose={(i) => advance(i)}
                  />
                ) : (
                  <Controls
                    isPlaying={audio.isPlaying}
                    onPlayPause={audio.toggle}
                    onNext={() => advance()}
                    onReplay={audio.restart}
                    isMuted={audio.muted}
                    onToggleMute={audio.toggleMute}
                    ended={audio.ended}
                    // No next scene → show a finish button, never auto-advance into
                    // comprehension (a context switch a child shouldn't trip into).
                    isLastScene={isLastScene}
                  />
                ))}
            </>
          )}

          {phase === "comprehension" && story?.comprehension && telemetry && (
            <Comprehension
              questions={story.comprehension}
              onAnswered={(qId, i, correct) => {
                if (correct) {
                  sound.chime();
                  hapticTick();
                }
                void telemetry.questionAnswered(qId, i, correct);
              }}
              onSkipped={(qId) => void telemetry.questionSkipped(qId)}
              onDone={() => completeAndEnd(path)}
            />
          )}

          {phase === "end" && (
            <div className="end-screen">
              <NightSky count={nights} />
              <h1>Goodnight{config ? `, ${config.child}` : ""}.</h1>
              {story?.title && (
                <p className="end-memory">
                  Tonight you finished <em>{story.title}</em>
                  {choiceLabels.length > 0 && (
                    <>
                      {" "}
                      — and you chose <em>{choiceLabels.join(", ")}</em>
                    </>
                  )}
                  .
                </p>
              )}
              <div className="end-actions">
                <button
                  type="button"
                  className="btn btn--replay-soft"
                  onClick={replay}
                >
                  ↻ Read it again
                </button>
                {libraryCards.length > 0 && (
                  <button
                    type="button"
                    className="btn btn--replay-soft"
                    onClick={returnToLibrary}
                  >
                    <ShelfGlyph /> Back to the library
                  </button>
                )}
              </div>
            </div>
          )}
        </main>

        {/* A stray tap mid-story must not abandon the page — ask first. */}
        {confirmLeave && (
          <div className="leave-scrim" onClick={stayInStory}>
            <div
              className="leave-card"
              role="dialog"
              aria-modal="true"
              aria-label="Leave the story?"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="leave-title">Leave the story?</p>
              <p className="leave-sub">
                The book starts from the beginning next time.
              </p>
              <div className="leave-actions">
                <button type="button" className="btn leave-stay" onClick={stayInStory}>
                  Keep reading
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={returnToLibrary}
                >
                  To the library
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Parent-only export, via ?export=1 only. The goodnight screen itself
            stays clean — collection is the endpoint plus this escape hatch. */}
        {exportRequested && telemetry && (
          <div className="export-floating">
            <ExportPanel telemetry={telemetry} />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Behind everything: nightfall (fades in at the end), the living dusk
          canvas, then the edge veil above the content. */}
      <div
        className={`nightfall${phase === "end" ? " nightfall--on" : ""}`}
        aria-hidden="true"
      />
      <Stardust />
      <div className="ambient-veil" aria-hidden="true" />
      {content}
    </>
  );
}

function resolveSrc(src: string, assetBase: string): string {
  if (/^https?:\/\//.test(src) || src.startsWith("/")) return src;
  return `${assetBase}${src}`;
}

/** Three little books leaning on a shelf — the way back to the library. */
function ShelfGlyph() {
  return (
    <svg className="shelf-glyph" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 4.5h3.6V20H4zM9.4 4.5H13V20H9.4zM15.2 5.2l3.5-.8 3 14.9-3.5.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="app">
      <div className="centered">{children}</div>
    </div>
  );
}
